/*
  SwingStudio — the full-screen playback view that turns a Gemini swing
  analysis + MediaPipe pose scan into an interactive coaching experience:

    - Portrait video player at top with a transparent canvas overlay.
    - Yellow trail of the trailing-hand wrist across the whole swing
      (drawn as a fading dotted arc — the reference image's yellow arc).
    - Blue vertical alignment line dropped from the head down to the
      target-side foot at address, staying visible on all frames.
    - Red target circle on the wrist at the exact impact frame.
    - Numbered fault cards (from Gemini) that fade in on the video when
      the playhead crosses each fault's timestamp.
    - Bottom timeline: 6 stick-figure phase icons that snap the playhead
      when tapped, plus small severity dots along the scrubber.

  The pose scan takes ~5-10s on first render — we show a scan bar over
  the video while it runs. Playback works even if the scan fails; only
  the overlay disappears.

  Props:
    videoSrc:        blob or https URL of the swing video
    analysis:        { phase_timestamps, faults, ... } from /swings/analyze
    onClose:         optional — X button in top-right when set
*/
import { useEffect, useMemo, useRef, useState } from "react";
import { X } from "@phosphor-icons/react";
import { usePoseScan, LM } from "../hooks/usePoseScan";

const PHASES = [
  { id: "address",   label: "Address" },
  { id: "backswing", label: "Backswing" },
  { id: "top",       label: "Top" },
  { id: "downswing", label: "Downswing" },
  { id: "impact",    label: "Impact" },
  { id: "finish",    label: "Finish" },
];

// Tiny inline SVG stick-figure per phase — matches the reference image's
// bottom row. Kept as small components so the phase bar is self-contained.
function PhaseIcon({ phase, active, onClick }) {
  const cls = `h-11 w-11 rounded-full flex items-center justify-center transition-all ${
    active ? "bg-white text-black scale-110" : "bg-white/10 text-white/70 hover:bg-white/20"
  }`;
  return (
    <button
      type="button"
      onClick={onClick}
      className={cls}
      title={PHASES.find((p) => p.id === phase)?.label}
      data-testid={`phase-icon-${phase}`}
    >
      <StickFigure phase={phase} />
    </button>
  );
}

function StickFigure({ phase }) {
  // Same body outline, different arm/club angles per phase. Simple
  // enough to hand-draw without a sprite sheet.
  const arms = {
    address:   [{ x1: 12, y1: 10, x2: 14, y2: 18 }],
    backswing: [{ x1: 12, y1: 10, x2: 6,  y2: 6  }],
    top:       [{ x1: 12, y1: 10, x2: 4,  y2: 2  }],
    downswing: [{ x1: 12, y1: 10, x2: 18, y2: 4  }],
    impact:    [{ x1: 12, y1: 10, x2: 20, y2: 14 }],
    finish:    [{ x1: 12, y1: 10, x2: 20, y2: 2  }],
  }[phase] || [];
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <circle cx="12" cy="4" r="2" />
      <line x1="12" y1="6" x2="12" y2="14" />
      <line x1="12" y1="14" x2="9" y2="21" />
      <line x1="12" y1="14" x2="15" y2="21" />
      {arms.map((a, i) => <line key={i} {...a} />)}
    </svg>
  );
}

// Fault severity → { colour, glow } for the badge + timeline dot.
const SEV_STYLE = {
  red:   { bg: "#EF4444", text: "#fff", label: "Danger" },
  amber: { bg: "#F59E0B", text: "#111", label: "Watch" },
  green: { bg: "#10B981", text: "#fff", label: "Good" },
};

export default function SwingStudio({ videoSrc, analysis, onClose }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playing, setPlaying] = useState(false);

  // Kick off the one-shot pose scan as soon as the video element exists.
  const scan = usePoseScan(videoRef.current);

  // Keep currentTime in sync with the <video>. We drive rAF ourselves
  // because `timeupdate` fires at ~4 Hz which is too slow for the
  // canvas overlay to feel smooth.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;
    let raf = 0;
    const loop = () => {
      setCurrentTime(v.currentTime);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);

  // Redraw the pose overlay whenever the playhead moves or the scan
  // completes. Canvas is sized to match the video element's rendered
  // dimensions so keypoints (normalized 0-1) map correctly.
  useEffect(() => {
    const v = videoRef.current;
    const c = canvasRef.current;
    if (!v || !c) return;
    // Match canvas backing store to video's rendered size (device px)
    const dpr = window.devicePixelRatio || 1;
    const rect = v.getBoundingClientRect();
    if (c.width !== rect.width * dpr || c.height !== rect.height * dpr) {
      c.width = rect.width * dpr;
      c.height = rect.height * dpr;
      c.style.width = `${rect.width}px`;
      c.style.height = `${rect.height}px`;
    }
    const ctx = c.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, rect.width, rect.height);
    if (!scan.ready) return;

    // Yellow wrist-trail across the whole swing — the "arc". Draw once
    // per redraw as a fading dotted trail.
    const wristIdx = LM.RIGHT_WRIST;
    ctx.save();
    scan.frames.forEach((f, i) => {
      const w = f.kp[wristIdx];
      if (!w || (w.visibility ?? 0) < 0.35) return;
      const x = w.x * rect.width;
      const y = w.y * rect.height;
      // Fade the future part of the trail so past positions pop more.
      const isPast = f.t <= currentTime;
      ctx.globalAlpha = isPast ? 0.85 : 0.35;
      ctx.fillStyle = "#FBBF24"; // amber-400
      ctx.beginPath();
      ctx.arc(x, y, i % 3 === 0 ? 4 : 2.5, 0, Math.PI * 2);
      ctx.fill();
    });
    ctx.restore();

    // Blue vertical alignment line — from the head down to the average
    // ankle position, using the address-frame landmarks so it never
    // wobbles with the swing.
    const addressFrame = scan.frames[0];
    if (addressFrame) {
      const head = addressFrame.kp[LM.NOSE];
      const la = addressFrame.kp[LM.LEFT_ANKLE];
      const ra = addressFrame.kp[LM.RIGHT_ANKLE];
      if (head && (la || ra)) {
        const footY = ((la?.y || ra?.y) + (ra?.y || la?.y)) / 2;
        const x = head.x * rect.width;
        ctx.save();
        ctx.strokeStyle = "rgba(59,130,246,0.9)"; // blue-500
        ctx.lineWidth = 3;
        ctx.setLineDash([6, 6]);
        ctx.beginPath();
        ctx.moveTo(x, head.y * rect.height);
        ctx.lineTo(x, footY * rect.height);
        ctx.stroke();
        ctx.restore();
      }
    }

    // Red target circle at impact — draw when playhead is near impact.
    const impactT = scan.detected.impact || analysis?.phase_timestamps?.impact || 0;
    if (impactT && Math.abs(currentTime - impactT) < 0.35) {
      const impactKp = scan.landmarksAt(impactT);
      const w = impactKp?.[wristIdx];
      if (w) {
        const x = w.x * rect.width;
        const y = w.y * rect.height;
        ctx.save();
        // Outer red ring
        ctx.strokeStyle = "#EF4444";
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.arc(x, y, 18, 0, Math.PI * 2);
        ctx.stroke();
        // Inner white dot
        ctx.fillStyle = "#fff";
        ctx.beginPath();
        ctx.arc(x, y, 4, 0, Math.PI * 2);
        ctx.fill();
        // Solid red centre
        ctx.fillStyle = "#EF4444";
        ctx.beginPath();
        ctx.arc(x, y, 2, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }
    }
  }, [currentTime, scan.ready, scan.frames, scan.detected, analysis]);

  // Phase timestamps — prefer pose-detected top/impact if we have them,
  // fall back to Gemini's guesses. Guarantees monotonic order.
  const phaseTimes = useMemo(() => {
    const g = analysis?.phase_timestamps || {};
    return {
      address:   Number(g.address)   || 0,
      backswing: Number(g.backswing) || 0,
      top:       Number(scan.detected?.top ?? g.top) || 0,
      downswing: Number(g.downswing) || 0,
      impact:    Number(scan.detected?.impact ?? g.impact) || 0,
      finish:    Number(g.finish)    || 0,
    };
  }, [analysis, scan.detected]);

  // The active fault card = the fault whose timestamp we've most
  // recently crossed (played into). Sits on-screen until the next one.
  const faults = analysis?.faults || [];
  const activeFault = useMemo(() => {
    if (!faults.length) return null;
    let last = null;
    for (const f of faults) {
      if (currentTime >= f.timestamp - 0.05) last = f;
    }
    return last;
  }, [faults, currentTime]);

  // Snap to the phase moment when a phase icon is tapped.
  const jumpToPhase = (id) => {
    const t = phaseTimes[id];
    const v = videoRef.current;
    if (v && Number.isFinite(t) && t >= 0) {
      v.currentTime = t;
      v.pause(); setPlaying(false);
    }
  };

  const activePhase = useMemo(() => {
    // Which phase are we in right now? Whichever the playhead has most
    // recently crossed. Defaults to `address` before the video starts.
    let cur = "address";
    for (const p of PHASES) {
      if (currentTime >= (phaseTimes[p.id] || 0) - 0.05) cur = p.id;
    }
    return cur;
  }, [currentTime, phaseTimes]);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); }
    else          { v.pause(); setPlaying(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col" data-testid="swing-studio">
      {/* Video + overlays */}
      <div className="relative flex-1 flex items-center justify-center bg-black overflow-hidden">
        <video
          ref={videoRef}
          src={videoSrc}
          playsInline
          onLoadedMetadata={(e) => setDuration(e.currentTarget.duration || 0)}
          onEnded={() => setPlaying(false)}
          className="max-h-full max-w-full"
          data-testid="swing-studio-video"
        />
        <canvas
          ref={canvasRef}
          className="absolute inset-0 pointer-events-none"
          style={{ mixBlendMode: "screen" }}
          data-testid="swing-studio-canvas"
        />

        {/* Top bar */}
        <div className="absolute top-0 left-0 right-0 p-3 flex items-center justify-between bg-gradient-to-b from-black/70 to-transparent">
          <div className="text-white/90 text-sm font-heading font-bold tracking-wide" data-testid="swing-studio-title">
            Swing Studio
          </div>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="h-9 w-9 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70"
              aria-label="Close"
              data-testid="swing-studio-close"
            >
              <X size={18} weight="bold" />
            </button>
          )}
        </div>

        {/* Scan progress bar */}
        {(scan.status === "loading" || scan.status === "scanning") && (
          <div className="absolute top-14 left-4 right-4 bg-black/70 backdrop-blur-md rounded-xl px-3 py-2 text-white text-xs" data-testid="swing-studio-scan-progress">
            <div className="flex items-center justify-between mb-1.5">
              <span className="font-heading font-bold uppercase tracking-wider">
                {scan.status === "loading" ? "Loading pose model" : "Analysing swing frames"}
              </span>
              <span className="opacity-70">{Math.round(scan.progress * 100)}%</span>
            </div>
            <div className="h-1 bg-white/20 rounded-full overflow-hidden">
              <div className="h-full bg-yellow-400 transition-all" style={{ width: `${scan.progress * 100}%` }} />
            </div>
          </div>
        )}

        {/* Fault callout — appears on the video like the reference */}
        {activeFault && (
          <div
            key={activeFault.n}
            className="absolute top-1/3 left-4 right-4 flex items-start gap-2 animate-fadein"
            data-testid={`swing-studio-fault-${activeFault.n}`}
          >
            <div
              className="h-8 w-8 rounded-full flex items-center justify-center font-bold text-sm shrink-0"
              style={{
                background: SEV_STYLE[activeFault.severity]?.bg || "#F59E0B",
                color: SEV_STYLE[activeFault.severity]?.text || "#111",
              }}
            >
              {activeFault.n}
            </div>
            <div className="flex-1 bg-white/95 rounded-xl px-3 py-2 shadow-lg">
              <div className="font-heading font-bold text-sm text-gray-900 leading-tight">{activeFault.title}</div>
              <div className="text-xs text-gray-700 leading-tight">{activeFault.detail}</div>
            </div>
          </div>
        )}

        {/* Big centre play/pause button */}
        <button
          type="button"
          onClick={togglePlay}
          className="absolute inset-0 flex items-center justify-center"
          aria-label={playing ? "Pause" : "Play"}
          data-testid="swing-studio-playpause"
        >
          {!playing && (
            <div className="h-16 w-16 rounded-full bg-white/80 flex items-center justify-center pointer-events-none">
              <div className="w-0 h-0 border-t-[10px] border-t-transparent border-b-[10px] border-b-transparent border-l-[16px] border-l-black ml-1" />
            </div>
          )}
        </button>
      </div>

      {/* Bottom controls: scrubber + phase icons */}
      <div className="bg-black/95 px-4 py-3 pb-safe">
        {/* Scrubber with fault dots */}
        <div className="relative h-8 flex items-center" data-testid="swing-studio-scrubber">
          <input
            type="range"
            min={0}
            max={Math.max(duration, 0.01)}
            step={0.01}
            value={Math.min(currentTime, duration)}
            onChange={(e) => {
              const t = Number(e.target.value);
              const v = videoRef.current;
              if (v) v.currentTime = t;
            }}
            className="w-full accent-yellow-400"
            aria-label="Playback position"
            data-testid="swing-studio-scrubber-input"
          />
          {/* Fault severity dots overlaid on the track */}
          <div className="pointer-events-none absolute inset-x-0 top-1/2 -translate-y-1/2 h-2">
            {faults.map((f) => {
              const pct = duration > 0 ? (f.timestamp / duration) * 100 : 0;
              return (
                <span
                  key={f.n}
                  className="absolute h-2.5 w-2.5 rounded-full border border-black"
                  style={{
                    left: `calc(${pct}% - 5px)`,
                    background: SEV_STYLE[f.severity]?.bg || "#F59E0B",
                  }}
                  data-testid={`scrubber-dot-${f.n}`}
                />
              );
            })}
          </div>
        </div>
        <div className="flex justify-between text-[10px] text-white/60 font-mono mb-2">
          <span data-testid="swing-studio-time">{currentTime.toFixed(1)}s</span>
          <span>{duration.toFixed(1)}s</span>
        </div>

        {/* Phase icons row */}
        <div className="flex items-center justify-between gap-1" data-testid="swing-studio-phases">
          {PHASES.map((p) => (
            <PhaseIcon
              key={p.id}
              phase={p.id}
              active={activePhase === p.id}
              onClick={() => jumpToPhase(p.id)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
